import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import Config from "../utils/Config";
import { formatDateTime } from "../utils/functions";

const outcomeLabel = outcome => outcome === "SMTP_ACCEPTED" ? "Email sent" : outcome === "FAILED" ? "Definitely failed · Not sent/accepted" : "Outcome unknown · Do not automatically resend";
const replyConfirmation = outcome => outcome === "SMTP_ACCEPTED" ? "Reply saved. Email sent." : "Reply saved. Notification: " + outcomeLabel(outcome);
export default function SupportTicketActions({ initialTicket, onUpdate }) {
  const { user } = useContext(AuthContext);
  const storageKey = "ffls-support-reply:" + (user?.id || user?._id) + ":" + initialTicket._id;
  const [savedDraft] = useState(() => {
    try { const value = JSON.parse(sessionStorage.getItem(storageKey)); return value && typeof value.message === "string" && value.message.length <= 2000 ? value : null; } catch { return null; }
  });
  const [draft, setDraft] = useState(savedDraft?.message || "");
  const [operation, setOperation] = useState(savedDraft?.key ? savedDraft : null);
  const [ticket, setTicket] = useState(initialTicket);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [reload, setReload] = useState(0);
  const [replyPage, setReplyPage] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [lastOutcome, setLastOutcome] = useState(null);
  const active = useRef(null);
  const update = useRef(onUpdate); update.current = onUpdate;
  const endpoint = Config.API.API_BASE_URL + "/api/support/requests/" + initialTicket._id;
  const headers = () => ({ "Content-Type": "application/json", Authorization: "Bearer " + (localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || "") });
  const clearAccepted = () => { sessionStorage.removeItem(storageKey); setOperation(null); setDraft(""); };
  useEffect(() => {
    const controller = new AbortController(); active.current = controller;
    setLoading(true);
    (async () => {
      try {
        const query = new URLSearchParams();
        if (replyPage !== null) query.set("replyPage", String(replyPage));
        if (operation) query.set("operationKey", operation.key);
        const response = await fetch(endpoint + (query.size ? "?" + query : ""), { headers: headers(), signal: controller.signal });
        if (!response.ok) throw new Error();
        const data = await response.json();
        if (!data.ticket || data.ticket._id !== initialTicket._id || !data.replyEligibility) throw new Error();
        if (controller.signal.aborted) return;
        setPagination(data.pagination || null); setTicket(data.ticket); setEligibility(data.replyEligibility); update.current({ status: data.ticket.status });
        const existing = operation && (data.recoveredReply || data.ticket.replies?.find(reply => reply.idempotencyKey === operation.key));
        if (existing && existing.message === operation.message.trim() && (!existing.authorRole || existing.authorRole === "ADMIN")) {
          setLastOutcome(existing.attemptOutcome);
          setFeedback({ error: existing.attemptOutcome !== "SMTP_ACCEPTED", text: replyConfirmation(existing.attemptOutcome) });
          clearAccepted();
        }
      } catch {
        if (!controller.signal.aborted) setFeedback({ error: true, text: "Unable to load reply history and authorization. Refresh ticket details before proceeding." });
      } finally {
        if (active.current === controller) active.current = null;
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => { controller.abort(); active.current?.abort(); };
    // Reload is explicit; drafts/operations must not trigger a second request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, reload, replyPage]);
  const act = async (action, event) => {
    event?.preventDefault();
    if (active.current || busy || loading) return;
    let attempt = operation;
    if (action === "replies") {
      if (!draft.trim() || draft.length > 2000) { setFeedback({ error: true, text: "Enter a reply of 1–2,000 characters." }); return; }
      try {
        if (!attempt) attempt = { key: window.crypto.randomUUID(), message: draft };
        sessionStorage.setItem(storageKey, JSON.stringify(attempt));
        setOperation(attempt);
      } catch { setFeedback({ error: true, text: "Unable to preserve the reply operation key. Nothing was submitted." }); return; }
    }
    const controller = new AbortController(); active.current = controller; setBusy(true); setFeedback(null);
    try {
      const response = await fetch(endpoint + "/" + action, { method: "POST", headers: headers(),
        body: JSON.stringify(action === "replies" ? { message: attempt.message, idempotencyKey: attempt.key } : {}), signal: controller.signal });
      const data = await response.json().catch(() => null);
      if (controller.signal.aborted) return;
      if (!response.ok) throw new Error(typeof data?.message === "string" ? data.message : "Operation could not be confirmed. Refresh ticket details before retrying.");
      if (action === "replies") {
        if (!data?.reply || data.reply.idempotencyKey !== attempt.key) throw new Error("Reply outcome could not be confirmed. Keep the same operation key.");
        const reply = data.reply;
        setTicket(current => ({ ...current, replies: [...(current.replies || []).filter(item => item.idempotencyKey !== reply.idempotencyKey), reply] }));
        setLastOutcome(reply.attemptOutcome);
        setFeedback({ error: reply.attemptOutcome !== "SMTP_ACCEPTED", text: replyConfirmation(reply.attemptOutcome) });
        if (data.conversationSaved === true || data.replay === true || reply.attemptOutcome === "SMTP_ACCEPTED") {
          clearAccepted();
          if (pagination) { setReplyPage(null); setReload(value => value + 1); }
        }
      } else {
        if (!data?.ticket?.status) throw new Error("Ticket status could not be confirmed.");
        setTicket(current => ({ ...current, ...data.ticket })); update.current({ status: data.ticket.status });
        setFeedback({ error: false, text: data.ticket.status === "CLOSED" ? "Ticket closed." : "Ticket reopened." });
      }
    } catch (failure) {
      if (!controller.signal.aborted) setFeedback({ error: true, text: (failure.message || "Operation could not be confirmed.") + (action === "replies" ? " Draft and operation key preserved. Refresh ticket details before retrying." : "") });
    } finally {
      if (active.current === controller) active.current = null;
      if (!controller.signal.aborted) setBusy(false);
    }
  };
  return <section className="support-ticket-actions" aria-label="Ticket replies and resolution">
    {loading && <p role="status">Loading reply history…</p>}
    {feedback && <p role={feedback.error ? "alert" : "status"}>{feedback.text}</p>}
    <div className="support-ticket-controls">
      <button type="button" disabled={busy || loading} onClick={() => setReload(value => value + 1)}>Refresh Ticket Details</button>
      <button type="button" disabled={busy || loading} onClick={() => act(ticket.status === "CLOSED" ? "reopen" : "close")}>{ticket.status === "CLOSED" ? "Reopen Ticket" : "Close Ticket"}</button>
    </div>
    <h3>Reply history</h3>
    {!(ticket.replies || []).length ? <p>No administrator replies yet.</p> : <ol>{(pagination ? ticket.replies : [...ticket.replies].sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt))).map(reply => <li key={reply.idempotencyKey}>
      {reply.authorRole === "DEALER" ? <p>{formatDateTime(reply.createdAt)} · Author: Dealer</p> : <>
        <p><strong>{outcomeLabel(reply.attemptOutcome)}</strong></p>
        <p>{formatDateTime(reply.createdAt)} · Administrator: {typeof reply.adminDisplayName === "string" && reply.adminDisplayName.trim() ? reply.adminDisplayName : "Former administrator"}</p>
        <p>To: {reply.recipientEmail}{reply.recipientSource === "current-account" ? " (current account email)" : ""}</p>
      </>}
      <p className="support-message-body">{reply.message}</p>
      {reply.authorRole !== "DEALER" && reply.outcomeNote && <small>{reply.outcomeNote}</small>}
    </li>)}</ol>}
    {pagination && pagination.totalPages > 1 && <nav className="support-request-pagination" aria-label="Reply history pagination">
      <button type="button" disabled={busy || loading || !pagination.hasPrevious} onClick={() => setReplyPage(pagination.page - 1)}>Previous</button>
      <span aria-live="polite">Page {pagination.page} of {pagination.totalPages}</span>
      <button type="button" disabled={busy || loading || !pagination.hasNext} onClick={() => setReplyPage(pagination.page + 1)}>Next</button>
    </nav>}
    {ticket.status === "CLOSED" ? <p>Reopen this ticket before replying.</p> : <>
      {eligibility && !eligibility.allowed && <p role="alert">{eligibility.reason}</p>}
      {eligibility?.allowed && <p>Reply to: {eligibility.recipientEmail}</p>}
      <form onSubmit={event => act("replies", event)}>
        <label htmlFor="support-ticket-reply">Reply</label>
        <textarea id="support-ticket-reply" rows={5} maxLength={2000} required value={draft} disabled={busy || loading} readOnly={Boolean(operation)} onChange={event => {
          setDraft(event.target.value);
          try { sessionStorage.setItem(storageKey, JSON.stringify({ message: event.target.value })); } catch { /* submission requires successful key persistence */ }
        }} />
        <button type="submit" disabled={busy || loading || !eligibility?.allowed}>{busy ? "Working…" : "Send Reply"}</button>
        {operation && <small>The saved operation key will be reused. Refresh ticket details to check an uncertain outcome.</small>}
        {operation && lastOutcome === "FAILED" && <button type="button" disabled={busy} onClick={() => { sessionStorage.setItem(storageKey, JSON.stringify({ message: draft })); setOperation(null); setLastOutcome(null); }}>Start New Reply Attempt</button>}
      </form>
    </>}
  </section>;
}
